import { useEffect, useState } from 'react'

import { Box, RadioButton, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { OneModal } from '@factorialco/f0-react/dist/experimental'
import { AvailableModules } from 'generated/featureDefinitions'

import { track } from 'modules/core/lib/Tracking/tracker'
import i18n from 'modules/core/lib/i18n'

import { isValidModule } from 'modules/marketplace_modules/lib/typesValidation'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { events } from 'modules/upsell/lib/events'
import { useCreateUpsellRequestWithErrorHandling } from 'modules/upselling_kit/hooks/useCreateUpsellRequest'
import { readLatestUpsellOrigin } from 'modules/upselling_kit/utils/localStorage'

interface CloseMeetingProps {
  isOpen: boolean
  onClose: () => void
  onUpsellRequestComplete?: (success: boolean) => void
  moduleRequested?: AvailableModules
  requestSource?: string
  pageSource?: string
  owner?: string
}

type OptionValue = 'yes' | 'no'

export const CloseMeeting = ({
  isOpen,
  onClose,
  onUpsellRequestComplete,
  moduleRequested,
  requestSource = 'From marketplace meeting booking',
  pageSource = window.location.pathname,
  owner,
}: CloseMeetingProps) => {
  const [selectedOption, setSelectedOption] = useState<OptionValue>('yes')
  const { create } = useCreateUpsellRequestWithErrorHandling()
  const validChargebeeModuleName = isValidModule(moduleRequested) ? moduleRequested : undefined

  const handleSubmit = async () => {
    if (selectedOption === 'yes') {
      const latestUpsellOrigin = readLatestUpsellOrigin()
      const currentCategory = latestUpsellOrigin?.request_source_category || 'Organic'
      const currentComponent = latestUpsellOrigin?.request_source_component
      const currentRequestSource = latestUpsellOrigin?.request_source || requestSource

      try {
        await create({
          moduleRequested: validChargebeeModuleName,
          requestSource: `book-a-meeting-exit-modal_${currentRequestSource}`,
          requestSourceCategory: currentCategory,
          requestSourceComponent: currentComponent,
          requestReason: ValidReasons.MissingAddon,
          pageSource,
          upsellRequestStatus: 'Opportunity detected',
          owner,
          upsellRequestComponentType: 'hubspot_iframe_meeting_booking',
          upsellRequestType: 'Book a meeting exit modal',
        })
        onClose()
        onUpsellRequestComplete?.(true)
      } catch (error) {
        onClose()
        onUpsellRequestComplete?.(false)
      }
    } else {
      onClose()
    }
  }

  const handleClose = () => {
    onClose()
  }

  useEffect(() => {
    if (isOpen) {
      track(events.viewBookAMeetingExitModal, {
        moduleRequested: validChargebeeModuleName,
        requestSource,
        pageSource,
      })
    }
  }, [isOpen, validChargebeeModuleName, requestSource, pageSource])

  return (
    <OneModal isOpen={isOpen} onClose={handleClose}>
      <OneModal.Header title={i18n.t('upselling_kit.book_a_meeting.title')} />
      <Box style={{ borderTop: '1px solid #F2F4F6' }}>
        <OneModal.Content withPadding>
          <Box gap="s16" flexDirection="column" paddingY="s12">
            <Text.Normal color="grey700">
              {i18n.t('upselling_kit.book_a_meeting.description')}
            </Text.Normal>
            <Box gap="s8" flexDirection="column">
              <Box gap="s8" flexDirection="row" alignItems="center">
                <RadioButton
                  checked={selectedOption === 'yes'}
                  onChange={() => setSelectedOption('yes')}
                  value="yes"
                  size="small"
                />
                <Text.Normal>{i18n.t('upselling_kit.book_a_meeting.yes')}</Text.Normal>
              </Box>
              <Box gap="s8" flexDirection="row" alignItems="center">
                <RadioButton
                  checked={selectedOption === 'no'}
                  onChange={() => setSelectedOption('no')}
                  value="no"
                  size="small"
                />
                <Text.Normal>{i18n.t('upselling_kit.book_a_meeting.no')}</Text.Normal>
              </Box>
            </Box>
          </Box>
        </OneModal.Content>
      </Box>
      <OneModal.Footer>
        <Box gap="s8" flexDirection="row" justifyContent="flexEnd" width="full">
          <F0Button
            onClick={handleSubmit}
            type="primary"
            label={i18n.t('upselling_kit.book_a_meeting.submit')}
          />
        </Box>
      </OneModal.Footer>
    </OneModal>
  )
}
